/*
 * Copyright (c) 2008-2020 by teambits GmbH, Darmstadt, Germany (http://www.teambits.de). All rights reserved.
 * This is CONFIDENTIAL code. Use is subject to license terms.
 */

/**
 * implement communication messages and setup server communication
 */

import { EventBus } from "~/common/src/event-bus" // or simply 'event-bus' (but this is not detected by IDEA)
import communication from "~/common/src/messaging" // or simply 'messaging' (but this is not detected by IDEA)
import storage from "~/common/src/utils/Storage" // or simply 'Storage' (but this is not detected by IDEA)
import clientInfo from "~/common/src/messaging/ClientInfo"
// required to be loaded before ServerCommunication
import { log } from "~/common/src/utils/log"
import { ACCESS_TOKEN_KEY } from "../auth/OAuth2AuthorizationCode"
import { FULL_HOST } from "../config/urls"
import store from "../store"
import { logout } from "@/auth/OAuth2AuthorizationCode"

window.baseURL = FULL_HOST

export default function registerCommunicationWithRouter(router) {
  router.beforeEach((to, from, next) => {
    log.info("Route changed ", from.fullPath, " to ", to.fullPath)
    // console.log(to)

    let id = ""
    if (to.name && to.name.startsWith("cockpit") && to.params && to.params.id) {
      id = to.params.id
    }
    if (id && !id.startsWith("MeetingModel_")) {
      id = "MeetingModel_" + id
    }

    if (clientInfo.meetingId !== id) {
      if (clientInfo.meetingId) {
        log.info("stopCommunication")
        communication.stopCommunication()
      }
      clientInfo.meetingId = id
      clientInfo.stationType = "WEB_COCKPIT"
      if (id) {
        log.info("startCommunication meetingId = ", id)

        // retrieve access token
        let accessToken = storage.getStorageObject(ACCESS_TOKEN_KEY)
        communication.startCommunication(clientInfo, accessToken.value)
      }
    }

    next()
  })
}

/**
 * return the meetingId of the meeting the app is currently connected to (or "" if none)
 *
 * The current meetingId can be found in the
 * @return {*}
 */
function getCurrentMeetingId() {
  let meetingId = clientInfo.meetingId
  if (!meetingId) {
    log.error("no meetingId defined ... why?")
    return null
  }
  if (meetingId.startsWith("MeetingModel_")) {
    meetingId = meetingId.substr(13)
  }
  return meetingId
}

communication.registerMessageHandlers({
  //Server informs the client that a previous message was not processed due to an AccessDeniedException
  AccessDeniedMessage: [handleAccessDeniedMessage],

  MeetingPropertiesMessage: [handleMeetingPropertiesMessage],
  MeetingUpdateMessage: [handleMeetingUpdateMessage],
  MeetingUpdatesMessage: [handleMeetingUpdatesMessage],
  ClientSettingsMessage: [handleClientSettingsMessage],
  InteractionsMessage: [handleInteractionsMessage],
  InsertInteractionMessage: [handleInsertInteractionMessage],
  UpdateInteractionMessage: [handleUpdateInteractionMessage],
  AsynchronousResetToolRequest: [handleResetToolRequestMessage],
  RemoveInteractionMessage: [handleRemoveInteractionMessage],
  MoveInteractionMessage: [handleMoveInteractionMessage],
  NavigateToIndexMessage: [handleNavigateToIndexMessage],
  ContributionsNotificationMessage: [handleContributionsNotificationMessage],
  ContributionsMessage: [handleContributionsMessage],
  ClearStageMessage: [handleClearStageMessage],
  StreamingInfoMessage: [handleStreamingInfoMessage],
  ResultMessage: [handleResultMessage],
  PublicationMessage: [handlePublicationMessage],
  InfoMessage: [handleInfoMessage],
  MeetingAutoCloserStatusMessage: [handleMeetingAutoCloserStatusMessage],
  CredentialGenerationStatusMessage: [handleCredentialGenerationStatusMessage],
})

communication.registerNonPersistentMessageTypes({
  UserSelectedMessage: true,
  UsersDataRequest: true, // the user list is sent to the client anyway once authenticated (and even if not ... well ...)
  ClientStatusMessage: true, // Activity Status
  ClientBatteryStatusMessage: true,
  NavigationMessage: true, // navigation doesn't make any sense if not online
  VoteContribution: true, // ensure that a user can't vote twice when offline, see #68389 ~54393
  OfflineFilesVersionChangedMessage: true, // will be sent automatically after authentication
})

function handleAccessDeniedMessage(message) {
  // TODO: Update to redirect to meeting list when websocket is closed due to access denial (DEV-2189)
  log.info(message)
}

function handleMeetingPropertiesMessage(message) {
  store.commit("meetings/setMeetingProperties", { id: getCurrentMeetingId(), properties: message.properties })
}

function handleMeetingUpdateMessage(message) {
  log.info("MeetingUpdateMessage received:", message.key, ":", message.value)
  store.commit("meetings/updateProperty", {
    id: getCurrentMeetingId(),
    key: message.key,
    value: message.value,
  })
}

function handleMeetingUpdatesMessage(message) {
  for (const [key, value] of Object.entries(message.updates)) {
    store.commit("meetings/updateProperty", {
      id: message.meetingId,
      key: key,
      value: value,
    })
  }
}

function handleClientSettingsMessage(message) {
  log.info("ClientSettingsMessage received: browsingEnabled: ", message.browsingEnabled)
  store.commit("meetings/setBrowsingEnabled", {
    meetingId: getCurrentMeetingId(),
    browsingEnabled: message.browsingEnabled,
  })
}

function handleInteractionsMessage(message) {
  log.info("InteractionsMessage received: ", message.interactions.length, "interactions")

  store.commit("meetings/updateInteractions", {
    meetingId: getCurrentMeetingId(),
    interactions: message.interactions,
  })
}

function handleInsertInteractionMessage(message) {
  log.info("InsertInteractionMessage received:", message)
  store.commit("meetings/insertInteraction", {
    meetingId: getCurrentMeetingId(),
    index: message.index,
    interaction: {
      config: message.config,
      interactionId: message.interactionId,
      type: message.type,
    },
  })
}

function handleUpdateInteractionMessage(message) {
  log.info("UpdateInteractionMessage received:", message)
  store.commit("meetings/updateInteraction", {
    interactionId: message.interactionId,
    config: message.config,
  })
}

function handleResetToolRequestMessage(message) {
  store.commit("contributions/resetInteraction", message)
}

function handleRemoveInteractionMessage(message) {
  log.info("RemoveInteractionMessage received:", message)
  store.commit("meetings/deleteInteraction", {
    meetingId: getCurrentMeetingId(),
    interactionId: message.interactionId,
  })
}

function handleMoveInteractionMessage(message) {
  log.info("MoveInteractionMessage received:", message)
  store.commit("meetings/moveInteraction", {
    meetingId: getCurrentMeetingId(),
    interactionId: message.interactionId,
    toIndex: message.toIndex,
  })
}

function handleNavigateToIndexMessage(message) {
  log.info("NavigateToIndexMessage received:", message)
  store.commit("meetings/navigate", {
    meetingId: getCurrentMeetingId(),
    index: message.toolIndex,
    phaseName: message.phaseName,
  })
}

function handleContributionsNotificationMessage(message) {
  store.commit("contributions/notificationReceived", message)
}

function handleContributionsMessage(message) {
  store.commit("contributions/storeContributions", message)
}

function handleClearStageMessage(message) {
  log.info("ClearStageMessage received:", message)
  store.commit("contributions/clearStage", message)
}

function handleStreamingInfoMessage(message) {
  log.info("StreamingInfoMessage received:", message)
  store.commit("meetings/setStreamingInfo", { meetingId: getCurrentMeetingId(), data: message.streamingInfo })
}

function handleResultMessage(message) {
  log.info("ResultMessage received: ", message)
  store.commit("rql/resultReceived", message)
}

function handlePublicationMessage(message) {
  log.info("PublicationMessage received: ", message)
  store.dispatch("publications/processIncomingPublication", { message })
}

function handleInfoMessage(message) {
  if (message.hasOwnProperty("infoCode") && message.infoCode === 671) {
    log.info("InfoMessage with code for remote logout received: ", message)
    logout()
  } else if (message.hasOwnProperty("infoCode") && message.infoCode === 265) {
    log.info("InfoMessage with code for successful connection received: ", message)
    // for handshake to be completed do somewhere
    EventBus.$emit("meeting:connectFinished", getCurrentMeetingId())
  } else {
    log.info("InfoMessage received that is not relevant for the MCC")
  }
}

function handleMeetingAutoCloserStatusMessage(message) {
  if (message.countdownAction === "START") {
    store.commit("meetings/setAutoCloseInMinutes", {
      id: getCurrentMeetingId(),
      autoCloseInMinutes: message.autoCloseInMinutes,
    })
  } else {
    store.commit("meetings/unsetAutoCloseInMinutes", {
      id: getCurrentMeetingId(),
    })
  }
}

function handleCredentialGenerationStatusMessage(message) {
  // todo DEV-3200: Support updating messages (progress, text and type)
  store.commit("statusMessages/hideAllWithMainMeetingId", message.mainMeetingId)
  store.commit("statusMessages/addStatusMessage", {
    status: message.status,
    icon: ["fas", "key"],
    message: message.message?.length > 0 ? message.message : null,
    messageCode: `credentialsGeneration.${message.status}`,
    mainMeetingId: message.mainMeetingId,
  })
}
